
import React, { useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { Region } from '../../../data/types/region-types';
import { useLanguage } from '../../../contexts/language';
import { 
  createChurchMarkerElement, 
  createChurchPopupContent,
  getChurchCoordinates
} from '../MapUtils';

interface ChurchMarkersProps {
  map: mapboxgl.Map | null;
  regions: Region[];
  filteredRegionIds?: string[];
  markerRefs: mapboxgl.Marker[];
}

const ChurchMarkers: React.FC<ChurchMarkersProps> = ({ 
  map, 
  regions, 
  filteredRegionIds,
  markerRefs 
}) => {
  const { t, language } = useLanguage();
  
  useEffect(() => {
    if (map && map.loaded()) {
      clearMarkers();
      addMarkers();
    }
    
    return () => {
      clearMarkers();
    };
  }, [regions, language, filteredRegionIds, map]);

  const clearMarkers = () => {
    // Clear church markers
    if (markerRefs) {
      markerRefs.forEach(marker => marker.remove());
      markerRefs.length = 0;
    }
  };

  const addMarkers = () => {
    if (!map) return;

    regions.forEach(region => {
      // Skip if region is filtered out
      if (filteredRegionIds && !filteredRegionIds.includes(region.region_id)) {
        return;
      }
      
      const regionName = language === 'en' ? region.english_name : region.spanish_name;
      
      // Add church markers for each church in the region
      if (region.church_funding && map) {
        region.church_funding.forEach((church, index) => {
          // Calculate position with slight offset to prevent overlap
          const churchCoords = getChurchCoordinates(region.coordinates, index);
          
          // Create church marker
          const churchEl = createChurchMarkerElement();
          
          // Create church popup content
          const churchPopupContent = createChurchPopupContent(church, regionName, t);
          
          // Create church popup
          const churchPopup = new mapboxgl.Popup({ 
            offset: 25,
            className: 'church-popup'
          }).setHTML(churchPopupContent);
          
          // Add marker to map
          const churchMarker = new mapboxgl.Marker(churchEl)
            .setLngLat(churchCoords)
            .setPopup(churchPopup)
            .addTo(map);
          
          // Add hover effects
          churchEl.addEventListener('mouseenter', () => {
            churchEl.style.transform = 'scale(1.1)';
          });
          
          churchEl.addEventListener('mouseleave', () => {
            churchEl.style.transform = 'scale(1)';
          });
          
          markerRefs.push(churchMarker);
        });
      }
    });
  };

  return null;
};

export default ChurchMarkers;
